// Per spec/components.md §6. Compact text-only member entry. Used by the
// member-list block (was welcome-new-members until P-037.16 D-116).
//
// **P-037.16 D-116 redesign** — no photo; the card is a stack of:
//   Name (16 px Helvetica bold; black text-primary by default, gold
//     #c69b34 anchor when URL is set — P-037.17 D-117 conditional color)
//   Company (14 px Helvetica bold text-primary)
//   Location (14 px Helvetica regular text-primary)
//   [optional partner section: "Spouse/Partner:" label + partner name 14 px bold]
//
// The leading divider above each card was the prior approach; the new
// member-list block hand-rolls a 3-col grid that emits ONE full-width
// divider per row of cards (not per card), so the divider is the grid's
// responsibility rather than the card's. Cards render no divider of their
// own. See `member-list.ts` for the grid + divider composition.
//
// The previous round-photo arm + partner-divider sub-section were dropped
// (Banks's PSD-source audit: text-only listing, no photos). The schema
// drop of `photoUrl` + `partnerLocation` rides the v2 → v3 migration that
// also renames the Block.

import { html } from '../render'
import { tokens } from '../tokens'
import { text } from '../primitives/text'
import { link } from '../primitives/link'
import { HtmlString } from '../types'

export interface MemberCardProps {
  name: string
  company: string
  location: string
  url?: string
  partnerName?: string
  /** Template-level link color from `RenderContext.linkColor` (P-037.18
   *  D-118). Used as the name anchor color when `url` is set. Defaults
   *  to `tokens.color['link-default']`. */
  linkColor?: string
}

// P-037.17 (D-117): only members WITH a URL render the name as an anchor;
// the no-URL arm renders the name in text-primary black instead. (P-037.18
// D-118: anchor color moved from inline #c69b34 to `props.linkColor` —
// template-level derivation from Header style.)
const NAME_SIZE = 16
// 12 px gap before the Spouse/Partner subsection.
const PARTNER_TOP_PAD = tokens.space[3]
// 4 px tight pairing between adjacent text rows (matches MemberCard's
// pre-P-037.16 inter-row rhythm).
const TIGHT_TOP_PAD = tokens.space[1]

function renderPartnerSection(partnerName: string): HtmlString {
  const labelHtml = text({ text: 'Spouse/Partner:', size: tokens.type.size.sm })
  const nameHtml = text({
    text: partnerName,
    size: tokens.type.size.sm,
    weight: tokens.type.weight.bold,
  })
  // prettier-ignore
  return html`<tr><td style="padding-top: ${PARTNER_TOP_PAD}px;">${labelHtml}</td></tr><tr><td style="padding-top: ${TIGHT_TOP_PAD}px;">${nameHtml}</td></tr>`
}

export function memberCard({
  name,
  company,
  location,
  url,
  partnerName,
  linkColor,
}: MemberCardProps): HtmlString {
  // Name: 16 px Helvetica bold. Color is conditional on URL presence
  // (P-037.17 D-117): URL → template-level link color (P-037.18 D-118
  // — was inline #c69b34); no URL → text-primary default <p>. Anchor's
  // font-size cascades from the wrapping <p>.
  const nameLinkColor = linkColor ?? tokens.color['link-default']
  const nameHtml =
    url !== undefined
      ? text({
          text: link({
            text: name,
            href: url,
            color: nameLinkColor,
            weight: tokens.type.weight.bold,
          }),
          size: NAME_SIZE,
          weight: tokens.type.weight.bold,
          color: nameLinkColor,
        })
      : text({ text: name, size: NAME_SIZE, weight: tokens.type.weight.bold })
  const companyHtml = text({
    text: company,
    size: tokens.type.size.sm,
    weight: tokens.type.weight.bold,
  })
  const locationHtml = text({ text: location, size: tokens.type.size.sm })
  const partnerSection =
    partnerName !== undefined ? renderPartnerSection(partnerName) : undefined

  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td>${nameHtml}</td></tr><tr><td style="padding-top: ${TIGHT_TOP_PAD}px;">${companyHtml}</td></tr><tr><td style="padding-top: ${TIGHT_TOP_PAD}px;">${locationHtml}</td></tr>${partnerSection}</table>`
}
